import { useEffect, useRef } from 'react';
import { Code, Palette, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Service {
  title: string;
  description: string;
}

export function Expertise() {
  const { t } = useTranslation();
  const servicesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target as HTMLElement;
          if (entry.isIntersecting) {
            element.classList.add('opacity-100', 'translate-y-0');
            element.classList.remove('opacity-0', 'translate-y-20');
          } else {
            element.classList.remove('opacity-100', 'translate-y-0');
            element.classList.add('opacity-0', 'translate-y-20');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    const handleScroll = () => {
      if (servicesRef.current) {
        const cards = Array.from(servicesRef.current.children);
        cards.forEach((card) => {
          const element = card as HTMLElement;
          const rect = element.getBoundingClientRect();
          const scrollPercent = (window.innerHeight - rect.top) / window.innerHeight;
          if (scrollPercent > 0 && scrollPercent < 1) {
            const translateY = (1 - scrollPercent) * 50;
            element.style.transform = `translateY(${translateY}px)`;
          }
        });
      }
    };

    const cards = servicesRef.current?.children;
    if (cards) {
      Array.from(cards).forEach((card, index) => {
        const element = card as HTMLElement;
        element.classList.add('opacity-0', 'translate-y-20', 'transition-all', 'duration-1000', 'ease-out');
        element.style.transitionDelay = `${index * 200}ms`;
        observer.observe(card);
      });
    }

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const icons = [
    <Code key="code" className="w-12 h-12" />,
    <Palette key="palette" className="w-12 h-12" />,
    <Globe key="globe" className="w-12 h-12" />
  ];

  // Définir un tableau fallback au cas où les traductions ne fonctionnent pas
  const fallbackServices: Service[] = [
    {
      title: "Développement Web",
      description: "Création de sites et applications web performants et responsives"
    },
    {
      title: "Design UI/UX",
      description: "Création d'interfaces utilisateur modernes et ergonomiques"
    },
    {
      title: "Solutions Internationales",
      description: "Adaptation de votre solution pour un public international"
    }
  ];

  // Récupérer les services depuis les traductions avec sécurité
  let services: Service[] = [];
  try {
    // Vérifier si la traduction retourne bien un tableau
    const translatedServices = t('services', { returnObjects: true });
    
    console.log("Translation 'services' result:", translatedServices);
    
    if (Array.isArray(translatedServices)) {
      // Vérifier que chaque élément du tableau correspond à l'interface Service
      services = translatedServices.filter((item): item is Service => 
        typeof item === 'object' && 
        item !== null && 
        'title' in item && 
        'description' in item &&
        typeof item.title === 'string' && 
        typeof item.description === 'string'
      );

      // Si après filtrage nous n'avons pas assez de services, utiliser les fallbacks
      if (services.length < fallbackServices.length) {
        console.warn("Some translated services don't match the required format");
        services = fallbackServices;
      }
    } else {
      console.warn("Translation 'services' is not an array:", translatedServices);
      services = fallbackServices;
    }
  } catch (error) {
    console.error("Error getting translations:", error);
    services = fallbackServices;
  }

  return (
    <section id="expertise" className="py-12 bg-white dark:bg-black text-gray-900 dark:text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('expertise_title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            {t('expertise_subtitle')}
          </p>
        </div>

        <div 
          ref={servicesRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-12"
        >
          {services.map((service: Service, index: number) => (
            <div
              key={index}
              className="bg-white dark:bg-black dark:from-black dark:to-black p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all will-change-transform"
            >
              <div className="text-blue-600 dark:text-blue-400 mb-6">
                {icons[index % icons.length]} {/* Utiliser modulo pour éviter les erreurs d'index */}
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {service.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}